/**
 * Marketplace Page - Main Orchestrator
 * Card grid layout with embedded settings per marketplace
 * Read-only pricing table with calculated values
 * Fulfillment toggle support (FBA/FBM)
 */

import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { CustomMarketplace } from '../../types/database';
import {
  saveCustomMarketplace,
  updateCustomMarketplace,
  deleteCustomMarketplace,
  generateId,
} from '../../utils/database';
import { useMarketplace } from './useMarketplace';
import MarketplaceGrid from './MarketplaceGrid';
import MarketplaceSettings from './MarketplaceSettings';
import MarketplacePricingTable from './MarketplacePricingTable';
import { useLanguage } from '../../i18n';
import { logger } from '../../utils/logger';

interface CustomMarketplacePageProps {
  initialMarketplaceId?: string;
}

const CustomMarketplacePage: React.FC<CustomMarketplacePageProps> = ({
  initialMarketplaceId,
}) => {
  // Use custom hook for state management
  const {
    marketplaces,
    setMarketplaces,
    selectedMarketplace,
    setSelectedMarketplace,
    selectedCountry,
    setSelectedCountry,
    selectedFulfillment,
    setSelectedFulfillment,
    products,
    setProducts,
    productData,
    viewMode,
    setViewMode,
    settingsTab,
    setSettingsTab,
    isLoading,
    isSaving,
    setIsSaving,
    exchangeRates,
    isLoadingRates,
    categoryDefaults,
    setCategoryDefaults,
    fulfillmentOptions,
    handleSelectMarketplace,
    handleBackToGrid,
    refreshRates,
  } = useMarketplace({ initialMarketplaceId });

  // i18n
  const { t } = useLanguage();

  // Local UI state
  const [isCreating, setIsCreating] = useState(false);
  const [newMarketplace, setNewMarketplace] = useState({ name: '', shortName: '', icon: '🛒', color: '#6366f1' });
  const [importStatus, setImportStatus] = useState<{ success: number; errors: number } | null>(null);

  // Create new marketplace
  const handleCreateMarketplace = async () => {
    if (!newMarketplace.name || !newMarketplace.shortName) return;

    try {
      const defaultColumns = [
        { id: generateId(), name: 'Platform Fee', key: 'platformFee', type: 'percent' as const, category: 'platform' as const, scope: 'category' as const, visible: true, editable: true, defaultValue: 15, order: 1 },
        { id: generateId(), name: 'Fulfillment Fee', key: 'fulfillmentFee', type: 'percent' as const, category: 'platform' as const, scope: 'category' as const, visible: true, editable: true, defaultValue: 12, order: 2 },
        { id: generateId(), name: 'Ads', key: 'ads', type: 'percent' as const, category: 'general' as const, scope: 'category' as const, visible: true, editable: true, defaultValue: 8, order: 3 },
        { id: generateId(), name: 'Shipping', key: 'shipping', type: 'fixed' as const, category: 'logistics' as const, scope: 'country' as const, visible: true, editable: true, defaultValue: 5, order: 4 },
      ];

      const marketplace = await saveCustomMarketplace({
        name: newMarketplace.name,
        shortName: newMarketplace.shortName.toUpperCase(),
        icon: newMarketplace.icon,
        color: newMarketplace.color,
        countries: [],
        columns: defaultColumns,
        fulfillmentOptions: ['Standard'],
        defaultFulfillment: 'Standard',
        isActive: true,
        displayOrder: marketplaces.length + 1,
      });

      setMarketplaces(prev => [...prev, marketplace]);
      setSelectedMarketplace(marketplace);
      setIsCreating(false);
      setNewMarketplace({ name: '', shortName: '', icon: '🛒', color: '#6366f1' });
      setViewMode('settings');
      setSettingsTab('countries');
    } catch (error) {
      logger.error('Failed to create marketplace:', error);
    }
  };

  // Save marketplace settings
  const handleSaveMarketplace = async () => {
    if (!selectedMarketplace) return;

    setIsSaving(true);
    try {
      await updateCustomMarketplace(selectedMarketplace);
      setMarketplaces(prev => prev.map(m => m.id === selectedMarketplace.id ? selectedMarketplace : m));
      setImportStatus({ success: 1, errors: 0 });
      setTimeout(() => setImportStatus(null), 2000);
    } catch (error) {
      logger.error('Failed to save marketplace:', error);
      setImportStatus({ success: 0, errors: 1 });
      setTimeout(() => setImportStatus(null), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete marketplace
  const handleDeleteMarketplace = async () => {
    if (!selectedMarketplace) return;
    if (!window.confirm(t.alerts.deleteConfirm.replace('{name}', selectedMarketplace.name))) return;

    try {
      await deleteCustomMarketplace(selectedMarketplace.id);
      setMarketplaces(prev => prev.filter(m => m.id !== selectedMarketplace.id));
      setSelectedMarketplace(null);
      setViewMode('grid');
    } catch (error) {
      logger.error('Failed to delete marketplace:', error);
    }
  };

  // Open settings for a marketplace
  const handleOpenSettings = (mp: CustomMarketplace) => {
    setSelectedMarketplace(mp);
    setViewMode('settings');
    setSettingsTab('general');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  // Grid View
  if (viewMode === 'grid') {
    return (
      <div className="space-y-6">
        <MarketplaceGrid
          marketplaces={marketplaces}
          onSelectMarketplace={handleSelectMarketplace}
          onOpenSettings={handleOpenSettings}
        />

        {/* Add New Marketplace Button */}
        <div className="flex justify-center">
          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-200"
            >
              <Plus className="w-4 h-4" />
              {t.marketplace.addNew}
            </button>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">{t.marketplace.newMarketplace}</h3>
                <button onClick={() => setIsCreating(false)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t.marketplace.name}</label>
                  <input
                    type="text"
                    value={newMarketplace.name}
                    onChange={(e) => setNewMarketplace(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. Trendyol"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t.marketplace.shortCode}</label>
                    <input
                      type="text"
                      value={newMarketplace.shortName}
                      onChange={(e) => setNewMarketplace(prev => ({ ...prev, shortName: e.target.value.toUpperCase() }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="TY"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t.marketplace.icon}</label>
                    <input
                      type="text"
                      value={newMarketplace.icon}
                      onChange={(e) => setNewMarketplace(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-center"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t.marketplace.color}</label>
                    <input
                      type="color"
                      value={newMarketplace.color}
                      onChange={(e) => setNewMarketplace(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full h-10 border border-slate-200 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>
                <button
                  onClick={handleCreateMarketplace}
                  disabled={!newMarketplace.name || !newMarketplace.shortName}
                  className="w-full py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50"
                >
                  {t.marketplace.create}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Settings View
  if (viewMode === 'settings' && selectedMarketplace) {
    return (
      <MarketplaceSettings
        selectedMarketplace={selectedMarketplace}
        products={products}
        categoryDefaults={categoryDefaults}
        settingsTab={settingsTab}
        isSaving={isSaving}
        importStatus={importStatus}
        setSelectedMarketplace={setSelectedMarketplace}
        setSettingsTab={setSettingsTab}
        setCategoryDefaults={setCategoryDefaults}
        setViewMode={setViewMode}
        setSelectedCountry={setSelectedCountry}
        onSave={handleSaveMarketplace}
        onDelete={handleDeleteMarketplace}
        onBackToGrid={handleBackToGrid}
      />
    );
  }

  // Table View
  if (viewMode === 'table' && selectedMarketplace) {
    return (
      <MarketplacePricingTable
        selectedMarketplace={selectedMarketplace}
        selectedCountry={selectedCountry}
        selectedFulfillment={selectedFulfillment}
        products={products}
        productData={productData}
        exchangeRates={exchangeRates}
        categoryDefaults={categoryDefaults}
        fulfillmentOptions={fulfillmentOptions}
        isLoadingRates={isLoadingRates}
        setSelectedCountry={setSelectedCountry}
        setSelectedFulfillment={setSelectedFulfillment}
        setViewMode={setViewMode}
        setSettingsTab={setSettingsTab}
        setProducts={setProducts}
        onBackToGrid={handleBackToGrid}
        onRefreshRates={refreshRates}
      />
    );
  }

  return null;
};

export default CustomMarketplacePage;
